/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/


import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, CircularProgress, Typography, useTheme } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useAuth } from "../../context/AuthContext";
import whiteLogo from "../../assets/white-logo.svg";

const NAV = [
  { label: "Dashboard", path: "/admin", Icon: DashboardOutlinedIcon },
  { label: "Members", path: "/admin/members", Icon: GroupOutlinedIcon },
  { label: "Team Folders", path: "/admin/teams", Icon: FolderSharedOutlinedIcon },
  { label: "Deleted Items", path: "/admin/trash", Icon: DeleteOutlineOutlinedIcon },
  { label: "Activity", path: "/admin/activity", Icon: HistoryOutlinedIcon },
];

/**
 * Org admin console. Mirrors the WorkDrive admin layout: a permanently dark
 * sidebar (regardless of app theme), a title bar with the org name, and a
 * "Back to team files" escape hatch.
 *
 * The guard here is convenience — every admin endpoint re-checks the caller's
 * role server-side, so routing alone is never the boundary.
 */
const AdminLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // isOrgAdmin comes from the org list AuthContext already loaded — asking
  // /drive/admin/me again re-read the same vcs_user_organisations.role. The
  // guard below is convenience; every admin endpoint re-checks server-side.
  const { activeOrg, activeOrgId, user, isOrgAdmin, isChecking } = useAuth();

  if (isChecking) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }
  if (!isOrgAdmin) return <Navigate to="/drive" replace />;

  const isActive = (path) =>
    path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(path);

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100%", overflow: "hidden" }}>
      {/* Always dark, matching the WorkDrive admin console, which does not
          follow the main app's light/dark setting. */}
      <Box sx={{ width: 260, flexShrink: 0, backgroundColor: "#101114", color: "#fff", display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: 3, py: 2 }}>
          <img src={whiteLogo} alt="Vecros" style={{ height: 28 }} />
        </Box>

        <Box component="ul" sx={{ listStyle: "none", p: 1.5, m: 0, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
          {NAV.map(({ label, path, Icon }) => {
            const active = isActive(path);
            return (
              <Box
                component="li"
                key={path}
                onClick={() => navigate(path)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.5,
                  py: 1.1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: 13.5,
                  backgroundColor: active ? "rgba(255,255,255,0.10)" : "transparent",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                }}
              >
                <Icon sx={{ fontSize: 18, opacity: 0.85 }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: active ? 600 : 500 }}>{label}</Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          onClick={() => navigate("/drive")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 3,
            py: 2,
            cursor: "pointer",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
          }}
        >
          <ArrowBackOutlinedIcon sx={{ fontSize: 18, opacity: 0.85 }} />
          <Typography sx={{ fontSize: 13 }}>Back to team files</Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", backgroundColor: theme.palette.primary.main }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            height: 56,
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.border.color}`,
            backgroundColor: theme.palette.secondary.main,
          }}
        >
          <Typography sx={{ fontSize: 19, fontWeight: 600, flex: 1, color: theme.palette.text.color }}>
            {NAV.find((n) => isActive(n.path))?.label || "Admin"}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.color }}>
            {activeOrg?.name || activeOrgId}
          </Typography>
          <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: "accent.solid" }}>
            {(user?.email || "?").charAt(0).toUpperCase()}
          </Avatar>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", color: theme.palette.text.color }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
